import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CellEditor, CellValue } from './TimecardCell';
import { FillLink } from './weekFill';
import { ExpandChevron } from '../TableRowExpansion';
import Tabs from '../ui/Tabs';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';
import useAnchoredPortalPanel from '../../hooks/useAnchoredPortalPanel';
import { STICKY_COLUMN_CELL_Z } from '../../constants/stickyTable';
import {
  GRID_FOLD, cellInputProblem, dayLabel, foldCellNeedsOpen, foldDaySummary, weekdayShort,
} from '../../utils/timesheetDisplay';
import { addDays, parseDate, toDateStr } from '../../utils/ganttDates';
import { formatDateDayMonth } from '../../utils/dateFormat';

/**
 * 🔢 The kinds whose typed text is REFUSED at the cell (input audit 2026-10-28): every hours row
 * (Camera O/T, Non-camera O/T, Travel) and Day #. Junk never reaches the draft or a confirm.
 */
const VALIDATED_KINDS = Object.freeze(['hours', 'count']);

/**
 * THE WEEK TIMECARD GRID — one shared grid for the unit timecard, a person's
 * week (the manager's expanded row) and "My timesheet" (owner, 2026-10-25:
 * *"build this and something similar for My time sheet. USE common code"*).
 * The reference is his own timecard screenshot (a named reference screen is
 * the spec): the seven days are COLUMNS — a small-caps day over a large light
 * date, Saturday/Sunday muted, TODAY outlined — and the fields are ROWS,
 * labelled on the left, in groups split by a rule.
 *
 * Presentational. The caller passes:
 *  - `dates` (7 × YYYY-MM-DD), `today`;
 *  - `groups` — `[[row, …], …]`, a rule between groups. A row is
 *    `{ key, label, kind, bold?, options? }`, kind ∈ time | hours | count |
 *    select | tick | text | computed. `options` (select) = `[{ value, label }]`;
 *    optional `editor` + `suggest` swap the editing control (`timesheetDisplay#fieldEditorProps`);
 *  - `getCell(row, date)` → `{ text, tone, conflict, drafted, own, editable,
 *    raw, title, srNote }` (see `TimecardCell.js`);
 *  - `onCommit(row, date, raw)` — an edit left the cell (a tick toggles
 *    straight away: `raw` is the new boolean);
 *  - `onConflict(row, date)` + `renderConflict({ row, date, close, edit })` —
 *    OPTIONAL: a conflict cell opens this chooser instead of the editor;
 *  - `fill` — the stepped `Fill →` / red `Clear` per row (`weekFill.js`), on
 *    every editable grid; a row with no editable cell shows none.
 *  - `rowAction(row)` — OPTIONAL trailing node per row, overriding `fill`;
 *  - `dayNote(date)` — OPTIONAL muted line under a day's date ("Away").
 *  - `dayComplete(date)` — OPTIONAL: true turns that day's heading GREEN with a ✓ (owner 2026-10-30:
 *    "turn green when completed enough") — the SERVER's `day.complete`, the same rule as the "2/7"
 *    count, so heading and count never disagree. Omitted (the unit timecard), every heading is as before.
 * 🗂️ The overtime-and-penalties group FOLDS (`GRID_FOLD`, owner 2026-10-25):
 * a group made of exactly those rows renders as one "O/T & penalties ▸" row,
 * collapsed by default, open by itself only for a conflict, a required blank
 * or an unsaved draft there (values ride in the folded summary). The
 * toggle is this grid's own state, never stored (no write on hydration), and
 * starts over on a new week.
 * 📱 The grid scrolls SIDEWAYS inside its own box with the label column
 * pinned (`sticky left-0`) — never the page.
 * ⚠️ jsdom has no layout: the outline, the pinned column and the sideways
 * scroll are browser checks (the S3b grid screenshots).
 */

// 📱 A narrower label column below `md` (152px is 40% of a 390 phone; measured 2026-10-25).
const GRID_COLS = 'grid-cols-[6.75rem_repeat(7,minmax(4.25rem,1fr))_auto] md:grid-cols-[9.5rem_repeat(7,minmax(4.75rem,1fr))_auto]';
/** Cell padding: none vertically from `md` (the row IS the cell's 28px, `TimecardCell`), 2px on a phone. */
const CELL_PAD = 'px-0.5 py-0.5 md:py-0';
/**
 * A row label, with a red * when the row must be filled in for a day to count (owner 2026-10-30:
 * "put a * next to items that need to be filled out"). The caller decides `row.required`; the star is
 * never the only channel — screen readers hear "required", and a blank required cell is marked too.
 */
function RowLabel({ row }) {
  if (!row.required) return row.label;
  return (
    <>
      {row.label}
      <span aria-hidden="true" title="Needed for the day to count" data-testid="row-required-star" className="ml-0.5 text-red-600">*</span>
      <span className="sr-only"> (required)</span>
    </>
  );
}

const LABEL_CELL =
  `sticky left-0 ${STICKY_COLUMN_CELL_Z} flex items-center bg-white pr-2 text-sm text-gray-600 ` +
  'dark:bg-gray-800 dark:text-gray-400';
// The fold row's one hover band — every cell of the row at once, the Breakdown group-header feel.
// OPAQUE on purpose: the label cell is sticky, so rows scrolling under it must not show through.
// 🪤 slate-100, not gray-100: index.css repaints `html.dark .bg-gray-100` as the card colour, which
// erased the band in dark mode (measured 2026-10-27).
// The 2px shadow in the same colour bridges the grid's `gap-x-0.5`, so the band reads as ONE strip
// with no column seams (owner 2026-10-27: "the table borders are showing when hovered").
// The rows a Flat week folds away (owner 2026-10-27) — a group made only of these becomes "Times ▸".
export const TIMES_FOLD_FIELDS = Object.freeze(['travel_to_hours', 'travel_to', 'unit_call', 'meal_start', 'meal_end', 'unit_wrap', 'travel_from_hours', 'travel_from', 'in_time', 'out_time']);
const FOLD_HOVER_BG ='bg-slate-100 dark:bg-gray-700 shadow-[4px_0_0_#f1f5f9] dark:shadow-[4px_0_0_#374151]';

/**
 * 📱 The phone day tabs with a SLIDING underline (owner 2026-10-31: "can you blue line under the day
 * scroll smoothly and follow the swiping action?"). Holds the bar's fractional position itself, so a
 * drag re-renders only this row, never the whole grid. The strip feeds it through `register`; between
 * drags (a tab tap, a new week) it rests on the active day.
 */
function SlidingDayTabs({ register, activeIndex, tabs, active, onChange, idPrefix }) {
  const [pos, setPos] = useState(activeIndex);
  const lastFed = useRef(0);
  useEffect(() => {
    register((p) => { lastFed.current = Date.now(); setPos(p); });
    return () => register(null);
  }, [register]);
  useEffect(() => {
    // A tab tap: the strip glides and FEEDS the bar; rest on the day only if nothing fed it (review F5).
    const t = setTimeout(() => { if (Date.now() - lastFed.current > 200) setPos(activeIndex); }, 250);
    return () => clearTimeout(t);
  }, [activeIndex]);
  return (
    <Tabs bare tabs={tabs} active={active} onChange={onChange} idPrefix={idPrefix}
      ariaLabel="Day" equal className="mb-1" indicatorAt={pos} />
  );
}

/** The phone strip's geometry, from the DAY panels (the end panels are narrower): `base` = Monday's
 * scroll position, `step` = one day, `max` = the furthest scroll. null before layout. */
function stripGeometry(el) {
  const days = [...el.children].filter((c) => c.hasAttribute('data-day'));
  if (days.length === 0) return null;
  const base = days[0].offsetLeft - el.children[0].offsetLeft;
  const step = days.length > 1 ? days[1].offsetLeft - days[0].offsetLeft : el.clientWidth;
  return step ? { base, step, max: el.scrollWidth - el.clientWidth } : null;
}

export default function WeekTimecardGrid({
  dates,
  today,
  groups,
  getCell,
  onCommit,
  onConflict,
  renderConflict,
  rowAction,
  fill = false,
  dayNote,
  dayComplete,
  idPrefix = 'ts-grid',
  ariaLabel = 'Week timecard',
  timesFold = null,
  // 📱 (owner 2026-10-31) `(dir) => void`, dir -1 / +1: the phone strip's END CARDS step the week through
  // the caller's own week change (which asks about unsaved edits). Absent = no end cards.
  onStepWeek = null,
  // false = no "Next week ›" end card (My timesheet's latest open week, owner 2026-11-01 ruling A).
  nextWeekOpen = true,
  requestedWeek = null, // the caller's week (Monday): differs from `dates[0]` while a step is loading
  // `{ date, n }` — bump `n` to take the person to that day (owner 2026-10-31: Submit week on an
  // unfinished week "takes you to the first unfinished day"): phone = pick it; desktop = scroll to it.
  jumpTo = null,
}) {
  const [editing, setEditing] = useState(null); // `${rowKey}|${date}`
  const [chooser, setChooser] = useState(null);
  const chooserRef = useRef(null);
  const anchorRef = useRef(null);
  // 🔑 The chooser is PORTALLED (the grid sits in its own sideways scroller,
  // which would clip an in-flow popover — `ui/Toolbar`'s "behind or gone"
  // rule) and placed by the shared `useAnchoredPortalPanel`; it closes on
  // scroll, outside click and Escape, and focus returns to the cell.
  const closeChooser = () => { setChooser(null); if (anchorRef.current) anchorRef.current.focus(); };
  const { coords, primeCoords } = useAnchoredPortalPanel({
    open: !!chooser, enabled: true, triggerRef: anchorRef, panelRef: chooserRef, onDismiss: () => setChooser(null),
  });

  useEffect(() => {
    if (!chooser) return undefined;
    const onDown = (e) => {
      if (chooserRef.current && !chooserRef.current.contains(e.target) && !(anchorRef.current && anchorRef.current.contains(e.target))) setChooser(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') closeChooser(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [chooser]); // eslint-disable-line react-hooks/exhaustive-deps

  // TWO folds: 'ot' (O/T & penalties, always) and 'times' (owner 2026-10-27: a Flat week folds the
  // time rows away) — the latter only when the caller passes `timesFold = { auto, label }`.
  const foldOf = (g) => {
    if (g.length === 0) return null;
    if (g.every((r) => GRID_FOLD.fields.includes(r.key))) return 'ot';
    if (timesFold && g.every((r) => TIMES_FOLD_FIELDS.includes(r.key))) return 'times';
    return null;
  };
  const isFold = (g) => foldOf(g) !== null;
  const otGroup = groups.find((g) => foldOf(g) === 'ot') || null;
  const foldAuto = {
    ot: !!otGroup && otGroup.some((r) => dates.some((d) => foldCellNeedsOpen(getCell(r, d)))),
    times: !!timesFold && timesFold.auto,
  };
  // Explicit toggles, kept for THIS week only (a new week starts from the data again).
  const [foldPick, setFoldPick] = useState(null); // { week, ot?, times? }
  const foldIsOpen = (id) => (foldPick && foldPick.week === dates[0] && foldPick[id] !== undefined ? foldPick[id] : foldAuto[id]);
  const [foldHover, setFoldHover] = useState(null); // fold id under the pointer
  const toggleFold = (id) => setFoldPick((cur) => ({ ...(cur && cur.week === dates[0] ? cur : {}), week: dates[0], [id]: !foldIsOpen(id) }));

  // 📱 PHONE (owner 2026-10-27, "One day at a time"): below `md` ONE day shows, picked from a day
  // strip (THE shared `ui/Tabs`, as My Orders) or by dragging the day strip (scroll-snap, 2026-10-31).
  const phone = useIsMobileOrderViewport();
  const [phoneDayPick, setPhoneDayPick] = useState(null);
  const phoneDay = phoneDayPick && dates.includes(phoneDayPick) ? phoneDayPick : (dates.includes(today) ? today : dates[0]);
  // A jump is the BUMP of `n`, never a re-render: the rest is read through a ref.
  const jumpN = jumpTo ? jumpTo.n : 0;
  const jumpCtx = useRef(null);
  jumpCtx.current = { jumpTo, dates, phone, idPrefix };
  useEffect(() => {
    const { jumpTo: j, dates: ds, phone: onPhone, idPrefix: pre } = jumpCtx.current;
    if (!j || !ds.includes(j.date)) return;
    if (onPhone) { setPhoneDayPick(j.date); return; }
    const el = document.querySelector(`[data-testid="${pre}-day-${j.date}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [jumpN]);
  // THE WEEK STRIP (owner 2026-10-27, the calendar pattern): all seven days on one row — weekday over
  // date, today's date ringed, an amber dot under a day that still needs something (a required blank
  // or a conflict). `ui/Tabs`' `equal` shares the row; the active day keeps Tabs' underline + weight.
  const needsAttention = (d) => phone && groups.some((g) => g.some((r) => { const c = getCell(r, d); return c && (c.required || c.conflict); }));
  const isDone = (d) => !!(dayComplete && dayComplete(d));
  // 🪤 `text-green-700` BARE: index.css repaints it in dark mode with `!important`, so a `dark:` pair is inert.
  const DONE_TEXT = 'text-green-700';
  const dayTabs = dates.map((d) => ({
    id: d,
    label: (
      <>
        <span className={`text-[0.6875rem] font-semibold uppercase tracking-wide ${isDone(d) ? DONE_TEXT : ''}`}>{isDone(d) ? '✓ ' : ''}{weekdayShort(d)}</span>
        <span className={`mt-0.5 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full text-base tabular-nums ${d === today ? 'ring-1 ring-blue-500 dark:ring-blue-400' : ''}`}>
          {Number(d.slice(8, 10))}
        </span>
        <span aria-hidden="true" className={`mt-0.5 h-1.5 w-1.5 rounded-full ${needsAttention(d) ? 'bg-amber-600 dark:bg-amber-400' : 'bg-transparent'}`} />
        {needsAttention(d) && <span className="sr-only">needs details</span>}
        {isDone(d) && <span className="sr-only">complete</span>}
      </>
    ),
  }));
  // 📱 THE DAY STRIP (owner 2026-10-31: "more of a contious scroll like desktop that kind of magnet snaps
  // to each timesheet"): the seven days sit side by side in ONE horizontal scroller with native
  // scroll-snap, so a drag follows the finger and lands on exactly one day. The tabs follow the strip
  // LIVE (the day nearest the centre), and a tab tap glides the strip there (instant under reduced
  // motion). `fromScroll` stops a day picked BY the strip from scrolling the strip back at itself.
  const stripRef = useRef(null);
  const fromScroll = useRef(false);
  const placed = useRef(null); // the week the strip was last positioned for (first place = no animation)
  // END PANELS (owner 2026-10-31, mock): with `onStepWeek`, a NARROW panel sits before Monday
  // ("‹ Previous week") and after Sunday ("Next week ›") — the owner: the full-width card was "too big, its
  // hard to see the text until youvve commited". Its text sits against the edge NEXT TO the days, so it
  // reads from the first pixels of a pull. Fully revealed = ARMED ("Let go for 2 Nov"); letting go there
  // asks the caller to change week (its own unsaved-changes question), and the new week opens on Monday
  // (forward) or Sunday (back). A partial pull snaps back to the edge day. Declined → glide back.
  const ends = phone && typeof onStepWeek === 'function';
  const pendingStep = useRef(0);
  const lastStepAt = useRef(0);
  const requestedRef = useRef(requestedWeek);
  requestedRef.current = requestedWeek;
  const landing = useRef(null); // the edge day a stepped-into week opens on, until placed
  const slideTabsTo = useRef(null); // set by SlidingDayTabs: (fractional day index) => void
  const registerSlide = useCallback((fn) => { slideTabsTo.current = fn; }, []);
  const fingerDown = useRef(false); // review F1: a step waits for the finger to LIFT
  const pendingFrom = useRef(null); // review F2: the week a pending step left
  const settleTimer = useRef(null);
  const backTimer = useRef(null);
  const [armed, setArmed] = useState(0); // -1 / +1 while an end panel is fully revealed
  const phoneIndex = dates.indexOf(phoneDay);
  const endAt = (el, g) => (!ends ? 0 : el.scrollLeft <= 2 && g.base > 2 ? -1
    : el.scrollLeft >= g.max - 2 && g.max > g.base + (dates.length - 1) * g.step + 2 ? 1 : 0);
  // A new week arrived after an end-panel step: open it at the edge the user came in from.
  const weekKey = dates[0];
  const datesRef = useRef(dates);
  datesRef.current = dates;
  // Before paint (a layout effect): the new week lands STRAIGHT on its edge day, hidden by the week slide.
  useLayoutEffect(() => {
    if (!pendingStep.current) return;
    const dir = pendingStep.current;
    pendingStep.current = 0;
    clearTimeout(backTimer.current);
    setArmed(0);
    placed.current = null; // so that placement is instant, not a glide
    const from = pendingFrom.current;
    pendingFrom.current = null;
    // A stale step (declined, then the header arrow went the OTHER way) picks no edge (review F2).
    if (from && (weekKey > from) !== (dir > 0)) return;
    const ds = datesRef.current;
    landing.current = dir > 0 ? ds[0] : ds[ds.length - 1];
    setPhoneDayPick(landing.current);
  }, [weekKey]);
  useEffect(() => () => { clearTimeout(settleTimer.current); clearTimeout(backTimer.current); }, []);
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!phone || !el) return;
    if (fromScroll.current) { fromScroll.current = false; return; }
    // The new week opens on its edge day: wait for that pick to apply, then jump there (no glide).
    if (landing.current) { if (phoneDay !== landing.current) return; landing.current = null; }
    const g = stripGeometry(el);
    if (!g) return;
    const left = g.base + phoneIndex * g.step;
    const first = placed.current !== weekKey;
    placed.current = weekKey;
    if (Math.abs(el.scrollLeft - left) < 2) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof el.scrollTo === 'function') el.scrollTo({ left, behavior: first || reduce ? 'auto' : 'smooth' });
    else el.scrollLeft = left;
    // Keyed on the WEEK (its Monday), never the `dates` array: a parent rebuilds that array on every
    // render, and re-placing on each one pulled the strip back mid-step (measured, 3b.7).
  }, [phone, phoneDay, phoneIndex, weekKey]);
  const glideTo = (el, left) => { if (typeof el.scrollTo === 'function') el.scrollTo({ left, behavior: 'smooth' }); else el.scrollLeft = left; };
  const onStripScroll = () => {
    const el = stripRef.current;
    if (!el) return;
    const g = stripGeometry(el);
    if (!g) return;
    const exact = Math.max(0, Math.min(dates.length - 1, (el.scrollLeft - g.base) / g.step));
    if (slideTabsTo.current) slideTabsTo.current(exact); // the day underline follows the finger
    const i = Math.round(exact);
    if (dates[i] !== phoneDay) { fromScroll.current = true; setPhoneDayPick(dates[i]); }
    if (!ends) return;
    const at = endAt(el, g);
    if (at !== armed) setArmed(at);
    // The scroll has SETTLED: on a fully revealed end panel → step the week; part-way into one → snap back.
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(settleStrip, 160);
  };
  // The strip has SETTLED (no scroll for a moment AND no finger on it): step, spring back, or nothing.
  // Review F1: at the very end the strip stops scrolling while the finger may still be down, so a step
  // also waits for touchend ("Let go for …" means exactly that).
  const settleStrip = () => {
    if (fingerDown.current) return;
    const s = stripRef.current;
    if (!s) return;
    const gs = stripGeometry(s);
    if (!gs) return;
    const edgeLeft = (d) => gs.base + (d > 0 ? dates.length - 1 : 0) * gs.step;
    const dir = endAt(s, gs);
    if (!dir) {
      if (s.scrollLeft < gs.base - 2) glideTo(s, edgeLeft(-1));
      else if (s.scrollLeft > edgeLeft(1) + 2) glideTo(s, edgeLeft(1));
      return;
    }
    // One step per pull: ignore a second settle on the same panel within a second.
    if (pendingStep.current === dir && Date.now() - lastStepAt.current < 1000) return;
    lastStepAt.current = Date.now();
    pendingStep.current = dir;
    const fromWeek = dates[0];
    pendingFrom.current = fromWeek;
    onStepWeek(dir);
    // The unsaved-changes question may be open: glide back to the edge day under it. The step stays
    // PENDING, so a later Save / Discard still opens the new week at the edge it was pulled from.
    clearTimeout(backTimer.current);
    const stepAt = Date.now();
    setTimeout(() => { if (pendingStep.current === dir && Date.now() - stepAt >= 60000) pendingStep.current = 0; }, 60000);
    backTimer.current = setTimeout(() => {
      const s2 = stripRef.current;
      if (!pendingStep.current || !s2) return;
      // The step went through and the new week is LOADING: stay on the panel (the week slide takes over).
      if (requestedRef.current && requestedRef.current !== fromWeek) return;
      const g2 = stripGeometry(s2);
      if (g2) glideTo(s2, g2.base + (dir > 0 ? dates.length - 1 : 0) * g2.step);
    }, 700);
  };
  const onStripTouchStart = () => { fingerDown.current = true; clearTimeout(settleTimer.current); };
  const onStripTouchEnd = () => {
    fingerDown.current = false;
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(settleStrip, 160); // momentum scroll events re-arm it
  };
  const nextMonday = toDateStr(addDays(parseDate(dates[0]), 7));
  const prevMonday = toDateStr(addDays(parseDate(dates[0]), -7));
  const endCard = (dir) => {
    const on = armed === dir;
    const when = formatDateDayMonth(dir > 0 ? nextMonday : prevMonday);
    return (
      <div key={dir > 0 ? 'next-week' : 'prev-week'} aria-hidden="true" data-end={dir > 0 ? 'next' : 'prev'}
        data-armed={on ? 'true' : undefined}
        data-testid={`${idPrefix}-end-${dir > 0 ? 'next' : 'prev'}`}
        className={`flex w-24 shrink-0 ${dir > 0 ? 'snap-end items-start pl-2 text-left' : 'snap-start items-end pr-2 text-right'} flex-col justify-start gap-1 self-stretch rounded-lg border-2 border-dashed pt-6 text-xs leading-tight ${on ? 'border-blue-500 text-blue-700 dark:border-blue-400 dark:text-blue-300' : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'}`}>
        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-base font-semibold ${on ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}>{dir > 0 ? '›' : '‹'}</span>
        {on ? (
          <span className="font-semibold">Let go for {when}</span>
        ) : (
          <>
            <span className="font-semibold">{dir > 0 ? 'Next week' : 'Previous week'}</span>
            <span>w/c {when}</span>
          </>
        )}
      </div>
    );
  };
  // A sideways swipe here changes DAY. Without this the browser ALSO reads it as "Back"
  // (Chrome's overscroll history swipe — owner 2026-10-27: a swipe landed him on Admin Settings).
  // The root scroller owns that gesture, so it is switched off on <html> while the phone grid shows.
  useEffect(() => {
    if (!phone || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const before = root.style.overscrollBehaviorX;
    root.style.overscrollBehaviorX = 'none';
    return () => { root.style.overscrollBehaviorX = before; };
  }, [phone]);

  const todayIndex = dates.indexOf(today);
  const rowCount = 1 + groups.reduce((n, g) => n + (isFold(g) ? 1 + (foldIsOpen(foldOf(g)) ? g.length : 0) : g.length), 0)
    + Math.max(0, groups.length - 1);

  const activate = (row, date, cell, e) => {
    const key = `${row.key}|${date}`;
    // A conflict opens the chooser; a cell that `opensPanel` (the unit timecard's Crew row) opens the same portalled slot.
    if ((cell.conflict || cell.opensPanel) && onConflict && renderConflict) {
      anchorRef.current = e ? e.currentTarget : null;
      primeCoords();
      setChooser((cur) => (cur === key ? null : key));
      return;
    }
    if (!cell.editable) return;
    if (row.kind === 'tick') { onCommit(row, date, !cell.raw); return; }
    setEditing(key);
  };

  const renderCell = (row, date) => {
    const key = `${row.key}|${date}`;
    const cell = getCell(row, date);
    const label = `${row.label}, ${dayLabel(date)}`;
    const testId = `${idPrefix}-cell-${row.key}-${date}`;
    if (editing === key) {
      return (
        <div key={key} role="cell" className={CELL_PAD}>
          <CellEditor
            // `row.editor` (Travel (h): 'suggest') swaps the CONTROL; `row.kind` stays what the value
            // is, so Fill, the cursor and the numbers-only rule still read it as hours.
            kind={row.editor || row.kind}
            validate={VALIDATED_KINDS.includes(row.kind) ? (text) => cellInputProblem(row.kind, text) : undefined}
            suggest={row.suggest}
            raw={cell.raw}
            options={row.options}
            label={label}
            testId={`${idPrefix}-edit-${row.key}-${date}`}
            // An unchanged commit drops — unless the cell opts in (`commitWhenEdited`, ruling 1A: a
            // following Pre-call / Final out) AND the user really typed; open-and-leave sends nothing.
            onCommit={(v, edited) => { setEditing(null); if (String(v) !== String(cell.raw == null ? '' : cell.raw) || (cell.commitWhenEdited && edited === true)) onCommit(row, date, v); }}
            onCancel={() => setEditing(null)}
          />
        </div>
      );
    }
    return (
      <div key={key} role="cell" className={`relative ${CELL_PAD}`}>
        <CellValue cell={cell} bold={row.bold} label={label} testId={testId} kind={row.kind}
          opensChooser={!!((cell.conflict || cell.opensPanel) && onConflict && renderConflict)} onActivate={(e) => activate(row, date, cell, e)} />
        {cell.onReset && (
          // ↺ — back to the calculation (a SIBLING of the cell's button, never nested in it).
          <button type="button" onClick={cell.onReset} title="Back to the calculated value" aria-label={`${label}: back to the calculated value`}
            data-testid={`${idPrefix}-reset-${row.key}-${date}`}
            className="absolute right-0.5 top-1/2 -translate-y-1/2 rounded px-0.5 text-[11px] leading-none text-blue-700 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-300 dark:hover:bg-blue-900/40">
            ↺
          </button>
        )}
        {chooser === key && createPortal(
          <div ref={chooserRef} style={{ position: 'fixed', top: coords ? coords.top : -9999, left: coords ? coords.left : -9999, zIndex: 60 }}>
            {renderConflict({
              row,
              date,
              close: closeChooser,
              edit: () => { setChooser(null); if (cell.editable) setEditing(key); },
            })}
          </div>,
          document.body
        )}
      </div>
    );
  };

  if (phone) {
    const note = dayNote ? dayNote(phoneDay) : null;
    const foldHeader = (g, day, active) => {
      const id = foldOf(g);
      const open = foldIsOpen(id);
      const label = id === 'times' ? timesFold.label
        : g.some((r) => GRID_FOLD.noteFields.includes(r.key)) ? GRID_FOLD.labelWithNotes : GRID_FOLD.label;
      const summary = open || id !== 'ot' ? null : foldDaySummary(g, day, getCell);
      const tid = id === 'ot' ? `${idPrefix}-fold` : `${idPrefix}-fold-${id}`;
      return (
        <button type="button" aria-expanded={open} data-testid={active ? `${tid}-toggle` : undefined} onClick={() => toggleFold(id)}
          className="flex min-h-[44px] w-full items-center gap-2 rounded-md px-1 text-left text-sm font-medium text-gray-800 active:bg-slate-100 dark:text-gray-100 dark:active:bg-gray-700">
          <ExpandChevron expanded={open} />
          <span className="shrink-0">{label}</span>
          {summary && <span className="ml-auto min-w-0 truncate text-xs font-normal text-gray-600 dark:text-gray-300">{summary}</span>}
        </button>
      );
    };
    return (
      <div data-testid={idPrefix} aria-label={ariaLabel} role="group">
        <SlidingDayTabs register={registerSlide} activeIndex={dates.indexOf(phoneDay)}
          tabs={dayTabs} active={phoneDay} onChange={setPhoneDayPick} idPrefix={`${idPrefix}-day`} />
        {note && <div className="px-1 pb-1 text-xs text-gray-600 dark:text-gray-400">{note}</div>}
        <div ref={stripRef} onScroll={onStripScroll} onTouchStart={onStripTouchStart} onTouchEnd={onStripTouchEnd}
          onTouchCancel={onStripTouchEnd} data-testid={`${idPrefix}-days-strip`}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {ends && endCard(-1)}
          {dates.map((day) => {
            const active = day === phoneDay;
            // Test ids (row, fold toggle, panel) only on the ACTIVE day — every cell already carries its date.
            return (
              <div key={day} role="group" aria-label={dayLabel(day)} data-day={day}
                className="w-full shrink-0 snap-start snap-always"
                data-testid={active ? `${idPrefix}-day-panel` : undefined}>
                {groups.map((g, gi) => (
                  <div key={g.map((r) => r.key).join('|')} className={gi > 0 ? 'mt-1 border-t-2 border-gray-100 pt-1 dark:border-gray-700' : ''}>
                    {isFold(g) && foldHeader(g, day, active)}
                    {(!isFold(g) || foldIsOpen(foldOf(g))) && g.map((row) => (
                      <div key={row.key} data-testid={active ? `${idPrefix}-row-${row.key}` : undefined} className="flex min-h-[44px] items-center gap-3 px-1">
                        <div className="w-36 shrink-0 text-sm text-gray-600 dark:text-gray-400"><RowLabel row={row} /></div>
                        <div className="min-w-0 flex-1">{renderCell(row, day)}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
          {ends && nextWeekOpen && endCard(1)}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto overscroll-x-contain" data-testid={`${idPrefix}-scroll`}>
      <div role="table" aria-label={ariaLabel} data-testid={idPrefix} className={`relative grid ${GRID_COLS} min-w-[38rem] md:min-w-[44rem] gap-x-0.5`}>
        {todayIndex >= 0 && (
          <div
            aria-hidden="true"
            data-testid={`${idPrefix}-today`}
            // ABSOLUTE on purpose: an absolutely-positioned grid child takes its
            // grid AREA as its box but plays no part in auto-placement, so the
            // cells beside it flow exactly as if it were not there.
            className="pointer-events-none absolute inset-0 z-0 rounded-lg border-[1.5px] border-blue-500/70 dark:border-blue-400/70"
            style={{ gridColumn: `${todayIndex + 2} / span 1`, gridRow: `1 / span ${rowCount}` }}
          />
        )}
        <div role="row" className="contents">
          <div role="columnheader" className={`${LABEL_CELL} row-start-1`} style={{ gridColumn: 1 }}><span className="sr-only">Field</span></div>
          {dates.map((d, i) => {
            const weekend = i >= 5;
            const note = dayNote ? dayNote(d) : null;
            const done = isDone(d);
            return (
              <div
                key={d}
                role="columnheader"
                aria-label={done ? `${dayLabel(d)}, complete` : dayLabel(d)}
                data-testid={`${idPrefix}-day-${d}`}
                data-complete={done ? 'true' : undefined}
                className="flex flex-col items-center pb-1 text-center"
                style={{ gridColumn: i + 2, gridRow: 1 }}
              >
                {/* ONE line, "MON 19" (owner 2026-10-25: fit a laptop screen). A complete day: green + ✓. */}
                <span className="flex items-baseline gap-1 whitespace-nowrap">
                  {done && <span aria-hidden="true" className={`text-xs font-semibold ${DONE_TEXT}`}>✓</span>}
                  <span className={`text-xs font-semibold uppercase tracking-wider ${done ? DONE_TEXT : weekend ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {weekdayShort(d)}
                  </span>
                  <span className={`text-base font-light tabular-nums ${done ? DONE_TEXT : weekend ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                    {Number(d.slice(8, 10))}
                  </span>
                </span>
                {note && <span className="text-xs text-gray-600 dark:text-gray-400">{note}</span>}
              </div>
            );
          })}
          <div style={{ gridColumn: 9, gridRow: 1 }} />
        </div>
        {groups.map((g, gi) => (
          <React.Fragment key={g.map((r) => r.key).join('|')}>
            {gi > 0 && <div role="presentation" className="col-span-full my-0.5 border-t border-gray-200 dark:border-gray-700" />}
            {isFold(g) && (() => {
              // Owner 2026-10-27: "the triangle … is not obvious … use the chevron? hover effect
              // on the section? look at breakdown" — the Breakdown group header's shape: the shared
              // `ExpandChevron` FIRST, and the WHOLE row is the target with one hover band.
              const id = foldOf(g);
              const open = foldIsOpen(id);
              const hover = foldHover === id;
              const toggle = () => toggleFold(id);
              const label = id === 'times' ? timesFold.label
                : g.some((r) => GRID_FOLD.noteFields.includes(r.key)) ? GRID_FOLD.labelWithNotes : GRID_FOLD.label;
              const tid = id === 'ot' ? `${idPrefix}-fold` : `${idPrefix}-fold-${id}`;
              return (
                <div role="row" className="contents" data-testid={tid}
                  onMouseEnter={() => setFoldHover(id)} onMouseLeave={() => setFoldHover(null)}>
                  {/* 🪤 index.css's `html.dark .bg-white { … !important }` outranks any hover class, so the
                      hover REPLACES the label cell's white rather than layering over it. */}
                  <div role="rowheader" className={`${hover ? LABEL_CELL.replace('bg-white', '').replace('dark:bg-gray-800', '') : LABEL_CELL} rounded-l-md ${hover ? FOLD_HOVER_BG : ''}`}>
                    <button type="button" aria-expanded={open} data-testid={`${tid}-toggle`}
                      className="flex min-h-[44px] md:min-h-[1.75rem] w-full items-center gap-1.5 text-left font-medium text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-100"
                      onClick={toggle}>
                      <ExpandChevron expanded={open} actionable={hover} />
                      {label}
                    </button>
                  </div>
                  {dates.map((d) => {
                    const text = open || id !== 'ot' ? null : foldDaySummary(g, d, getCell);
                    const what = id === 'ot' ? 'the O/T rows' : 'the times';
                    return (
                      <div key={d} role="cell" data-testid={`${tid}-summary-${d}`} title={text || (open ? `Hide ${what}` : `Show ${what}`)}
                        onClick={toggle}
                        className={`flex min-w-0 cursor-pointer items-center justify-center truncate px-0.5 text-xs text-gray-600 dark:text-gray-300 ${hover ? FOLD_HOVER_BG : ''}`}>
                        {text || ''}
                      </div>
                    );
                  })}
                  <div onClick={toggle} className={`cursor-pointer rounded-r-md ${hover ? FOLD_HOVER_BG : ''}`} />
                </div>
              );
            })()}
            {(!isFold(g) || foldIsOpen(foldOf(g))) && g.map((row) => (
              <div key={row.key} role="row" className="contents group" data-testid={`${idPrefix}-row-${row.key}`}>
                <div role="rowheader" className={LABEL_CELL}><RowLabel row={row} /></div>
                {dates.map((d) => renderCell(row, d))}
                <div className="flex items-center pl-1">{rowAction ? rowAction(row) : fill && !row.noFill && dates.some((d) => getCell(row, d).editable)
                  ? <FillLink row={row} dates={dates} getCell={getCell} onCommit={onCommit} idPrefix={idPrefix} />
                  : null}</div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
